import { FaArrowLeft } from "react-icons/fa";
import * as M from "./styled";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState, useRef } from "react";
import { RoleTypes } from "../../Constant/enums/roleTypes";
import { useDispatch, useSelector } from "react-redux";
import {
  clearExploreResults,
  fetchExploreList,
} from "../../Redux/slices/exploreSlice";
import { AppDispatch, RootState } from "../../Redux/store";
import UserMemberShip from "../../Components/Memberships";
import Suggesstion from "../../Components/Suggesstion";
import CreatorSkeleton from "../../Components/Skeletons/CreatorSkeleton";
import { ExploreList } from "../../Config/interface.config";
import { getUserDetails } from "../../Redux/slices/userSlice";
import { checkIsSubscribe, isCreatorRole } from "../../Config/common.config";
import SubscriptionModal from "../../Modals/SubscriptionModal";
import CommonLoader from "../../Components/CommonLoader";

const SEARCH_DEBOUNCE_MS = 400;

const Membership = () => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  const user = useSelector((state: RootState) => state.user);
  const explore = useSelector((state: RootState) => state.explore);
  const { loading, results, page, hasMore } = explore;
  const visibleCreators = results.filter(
    (creator) => creator?.isSuggested !== false,
  );

  const [open, setOpen] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState<ExploreList | null>(
    null,
  );

  const [search, setSearch] = useState("");
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const observerRef = useRef<HTMLDivElement | null>(null);

  /* ---------------- FETCH CREATORS ---------------- */
  const fetchCreators = useCallback(
    (pageToFetch: number, append = false, searchText = search) => {
      if (pageToFetch !== 1 && (loading || !hasMore)) return;

      dispatch(
        fetchExploreList({
          limit: 10,
          page: pageToFetch,
          isAppend: append,
          fullname: searchText || undefined,
          isFilterBySubscriber: true,
          role: RoleTypes.CREATOR,
        }),
      );
    },
    [dispatch, loading, hasMore, search],
  );

  /* ---------------- INIT (RESTORE OR FETCH ONCE) ---------------- */
  useEffect(() => {
    dispatch(getUserDetails());
    dispatch(clearExploreResults());
    fetchCreators(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- INFINITE SCROLL ---------------- */
  useEffect(() => {
    if (!observerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loading) {
          fetchCreators(page + 1, true);
        }
      },
      { threshold: 1 },
    );

    observer.observe(observerRef.current);
    return () => observer.disconnect();
  }, [page, hasMore, loading, fetchCreators]);

  /* ---------------- SEARCH (DEBOUNCED) ---------------- */
  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      dispatch(clearExploreResults());
      fetchCreators(1, false, value);
    }, SEARCH_DEBOUNCE_MS);
  };

  /* ---------------- SUBSCRIBE ---------------- */
  const onSubscribe = (creator: ExploreList) => {
    if (!user?.id) return;
    setSelectedCreator(creator);
    setOpen(true);
  };

  /* ---------------- INITIAL LOADER ---------------- */
  if (loading && page === 1 && results.length === 0) {
    return (
      <M.MemberShip>
        <div className="px-[5px] sm:px-[2rem]">
          <CommonLoader message="Fetching memberships..." />
        </div>
      </M.MemberShip>
    );
  }

  return (
    <M.MemberShip>
      <div className="px-[5px] sm:px-[2rem]">
        <h1 className="text-white font-noto flex items-center gap-5">
          <FaArrowLeft
            className="cursor-pointer"
            size={30}
            onClick={() => navigate("/account")}
          />
          <span className="text-[28px]">Membership</span>
        </h1>

        {/* -------- CURRENT SUBSCRIPTIONS -------- */}
        <div className="mt-[2rem] gap-10 flex flex-col">
          {user?.subscribeTo?.map((creator, i) => (
            <UserMemberShip
              key={creator._id || creator.id || i}
              creator={creator}
              onCancelSuccess={() => dispatch(getUserDetails())}
            />
          ))}
        </div>
      </div>

      {/* -------- RECOMMENDATIONS -------- */}
      <div className="mt-10 px-[5px] sm:px-[2rem]">
        <h3 className="text-lg sm:text-2xl">Recommendation</h3>

        <input
          value={search}
          onChange={onSearchChange}
          placeholder="Search creators"
          className="w-full mt-4 mb-6 bg-transparent border-b text-white"
        />

        <div className="mt-5">
          {visibleCreators
            .filter(
              (creator) =>
                !checkIsSubscribe(
                  creator._id || creator.id || "",
                  user.subscribeTo as any,
                ),
            )
            .map((creator, i) => (
              <div key={creator.id || i} className="mb-10">
                <Suggesstion creator={creator} subscribeCreator={onSubscribe} />
              </div>
            ))}

          {/* Skeletons while loading more */}
          {loading &&
            Array.from({ length: 3 }).map((_, i) => (
              <CreatorSkeleton key={`skeleton-${i}`} />
            ))}

          {/* Scroll sentinel */}
          <div ref={observerRef} style={{ height: 40 }} />
        </div>
      </div>

      {!isCreatorRole(user.role) && (
        <SubscriptionModal
          selectedCreator={selectedCreator}
          show={open}
          onHide={() => setOpen(false)}
          setRefreshFeedsWithCreatorList={() => {}}
        />
      )}
    </M.MemberShip>
  );
};

export default Membership;
